import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import PropTypes from "prop-types";
import Typography from "@mui/material/Typography";
import StatusChip from "components/StatusChip";
import Swal from "sweetalert2";
import { API_BASE } from "config/api";

const RecipientCell = ({ value }) => (
  <Typography variant="button" fontWeight={500}>
    {value}
  </Typography>
);
RecipientCell.propTypes = { value: PropTypes.string.isRequired };

const StatusCell = ({ value }) => (
  <StatusChip value={value} color={value === "sent" ? "success" : "error"} />
);
StatusCell.propTypes = { value: PropTypes.string.isRequired };

export default function useBulkEmailData() {
  const { church } = useAuth();

  const [members, setMembers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [composeOpen, setComposeOpen] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [isSending, setIsSending] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem("authToken");
    return token ? { Accept: "application/json", Authorization: `Bearer ${token}` } : null;
  };

  const columns = [
    {
      Header: "Recipient",
      accessor: "recipient",
      align: "left",
      width: "30%",
      Cell: RecipientCell,
    },
    { Header: "Subject", accessor: "subject", align: "left", width: "35%" },
    { Header: "Status", accessor: "status", align: "center", width: "15%", Cell: StatusCell },
    { Header: "Sent", accessor: "sentLabel", align: "left", width: "20%" },
  ];

  const fetchMembers = useCallback(async () => {
    if (!church?.id) return;
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/members/church/${church.id}`, { headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      const list = Array.isArray(result) ? result : result.data || [];
      setMembers(list.filter((m) => m.email));
    } catch (e) {
      console.error("Fetch members failed:", e);
    }
  }, [church?.id]);

  const fetchLogs = useCallback(async () => {
    if (!church?.id) return;
    setIsLoading(true);
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/v3/email/logs`, { headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      const list = result.data || [];
      setLogs(
        list.map((l) => ({
          id: l.id,
          recipient: l.recipient,
          subject: l.subject,
          status: l.status,
          sentLabel: new Date(l.created_at).toLocaleString(),
        }))
      );
      setError(null);
    } catch (e) {
      console.error("Fetch email logs failed:", e);
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [church?.id]);

  useEffect(() => {
    fetchMembers();
    fetchLogs();
  }, [fetchMembers, fetchLogs]);

  const handleCompose = () => {
    setSubject("");
    setMessage("");
    setSelectedMemberIds([]);
    setComposeOpen(true);
  };

  const handleSelectAll = () => {
    setSelectedMemberIds((prev) =>
      prev.length === members.length ? [] : members.map((m) => m.id)
    );
  };

  const handleSend = async () => {
    try {
      if (!subject.trim() || !message.trim()) throw new Error("Subject and message are required.");
      if (selectedMemberIds.length === 0) throw new Error("Select at least one recipient.");
      setIsSending(true);
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/v3/email/send-bulk`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ subject, message, member_ids: selectedMemberIds }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.message || `HTTP error! status: ${response.status}`);

      Swal.fire({
        title: "Sent!",
        text: `${body.data.sent} email(s) sent, ${body.data.failed} failed.`,
        icon: "success",
      });
      setComposeOpen(false);
      fetchLogs();
    } catch (e) {
      Swal.fire({ title: "Error!", text: e.message, icon: "error" });
    } finally {
      setIsSending(false);
    }
  };

  return {
    columns,
    rows: logs,
    isLoading,
    error,
    members,
    composeOpen,
    setComposeOpen,
    subject,
    setSubject,
    message,
    setMessage,
    selectedMemberIds,
    setSelectedMemberIds,
    isSending,
    handleCompose,
    handleSelectAll,
    handleSend,
  };
}
